import React, { ReactNode, useEffect, useState } from 'react'
import { UnderwritingOffer } from '_gql'
import { ApolloError } from '@apollo/client'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedCurrency, FormattedMessage, useIntl } from '@divvy-web/i18n'
import Badge, { BADGE_COLOR_ACTION } from '@divvy-web/skylab.badge'
import BasicButton, { BASIC_BUTTON_TYPE_FILLED, BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import Icon from '@divvy-web/skylab.icon'
import Tile, { TILE_SELECTED_COLOR_ACTION } from '@divvy-web/skylab.tile'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import AgreementTakeover from '@sputnik/components/AgreementTakeover'
import Divider from '@sputnik/components/Divider'
import { useAcceptTilaAgreement } from '@sputnik/pages/PostApplicationPages/ApplicationAgreements/AcceptTilaAgreement.gql'
import { GetCreditApplicationTilaInfo_creditApplication_CreditApplication as CreditApplicationType } from '@sputnik/pages/PostApplicationPages/ApplicationAgreements/GetCreditApplicationTilaInfo.gql'
import EligibleOfferTileLearnMoreLink from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/EligibleOfferTileLearnMoreLink/EligibleOfferTileLearnMoreLink'
import { GetEligibleOffers_decisions_Decision } from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/GetEligibleOffers.gql'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'
import { getOfferTypeTrackingLabel } from '@sputnik/utils/offerTypeTrackingLabel'

import styles from './styles.module.scss'

interface EligibleOfferTileProps {
  decision: GetEligibleOffers_decisions_Decision | null | undefined
  bulletPoints: ReactNode[]
  agreementInfo: {
    creditApplication: CreditApplicationType
    hasAgreedToTila: boolean
    hasTilaContent: boolean
    tilaInfoError: ApolloError | undefined
    isLoggedInUserAuthorizedSigner: boolean
  }
  description: ReactNode
  subtitle?: ReactNode
  title: ReactNode
  learnMoreLink: string
  setSelectedOffer: React.Dispatch<React.SetStateAction<GetEligibleOffers_decisions_Decision | null | undefined>>
  isSelected: boolean
}

const EligibleOfferTile = ({
  agreementInfo,
  decision,
  bulletPoints,
  description,
  subtitle,
  title,
  learnMoreLink,
  setSelectedOffer,
  isSelected,
}: EligibleOfferTileProps) => {
  const [getClassName, makeTestId] = useNameFormatter('EligibleOfferTile')
  const { formatMessage } = useIntl()
  const [isShowingAgreementTakeover, setIsShowingAgreementTakeover] = useState(false)
  const offerType = decision?.offerType
  const isMostPopular = offerType === UnderwritingOffer.FIXED_LINE

  const showToast = useToast()
  const { track } = useWebTracking()

  const { creditApplication, hasTilaContent, tilaInfoError, hasAgreedToTila, isLoggedInUserAuthorizedSigner } =
    agreementInfo || {}

  const tilaContent = creditApplication?.tila?.content

  const [acceptTilaAgreement, { loading: isAcceptingTilaAgreement }] = useAcceptTilaAgreement({
    onCompleted: () => {
      setIsShowingAgreementTakeover(false)
      setSelectedOffer(decision)

      logInfo('acceptTilaAgreement', {
        attributes: {
          creditAppId: creditApplication?.salesforceCreditId,
          result: 'accepts agreement terms for application',
        },
        eventName: 'eligibleOfferTile',
      })

      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='You have successfully agreed to your offer summary'
          id='sputnik.EligibleOfferTile__VFXi23'
        />,
      )
    },
    onError: (e) => {
      setIsShowingAgreementTakeover(false)

      track('se_signup__error_message__fired', {
        error_field: 'acceptTilaAgreement',
        error_message_text: e?.message || '',
      })

      logError(e, {
        attributes: {
          creditAppId: creditApplication?.salesforceCreditId,
          action: 'acceptTilaAgreement',
          message: e?.message,
          result: 'Error accepting agreement terms for application',
        },
        eventName: 'eligibleOfferTile',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='There was an error agreeing to your offer summary.'
          id='sputnik.EligibleOfferTile__9fJ3od'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  useEffect(() => {
    if (isShowingAgreementTakeover) {
      track('se_signup__eligible_offers_tila_agreement__presented', {
        offer_type: getOfferTypeTrackingLabel(offerType),
      })
    }
  }, [isShowingAgreementTakeover, offerType, track])

  const handleAgreeToOfferSummary = () => {
    offerType &&
      track('se_signup__eligible_offers_tila_agreement_agree__fired', {
        offer_type: getOfferTypeTrackingLabel(offerType),
      })
    if (offerType && creditApplication) {
      acceptTilaAgreement({
        variables: {
          salesforceCreditId: creditApplication?.salesforceCreditId as string,
          underwritingDecisionUuid: decision?.id,
          underwritingOfferType: offerType,
        },
        refetchQueries: ['GetTilaInfo'],
      })
    }
  }

  const handleCancelOfferSummaryClick = () => {
    setIsShowingAgreementTakeover(false)
    track('se_signup__eligible_offers_tila_agreement_cancel__fired', {
      offer_type: getOfferTypeTrackingLabel(offerType),
    })
  }

  const { approvedAmountInCents: approvedAmount } = decision || {}

  const hasApprovedAmmount = approvedAmount !== undefined && approvedAmount > 0

  return (
    <>
      <Tile
        hidePadding
        className={styles[getClassName('')]}
        dataTestId={makeTestId(offerType || '')}
        isSelected={isSelected}
        selectedColor={TILE_SELECTED_COLOR_ACTION}
      >
        <div className={styles[getClassName('inner')]}>
          <div className={styles[getClassName('content-wrapper')]}>
            <div className={styles[getClassName('title-wrapper')]}>
              {isMostPopular && (
                <Badge color={BADGE_COLOR_ACTION}>
                  <FormattedMessage
                    defaultMessage='Most popular'
                    id='sputnik.EligibleOfferTile__dC3u7Z'
                  />
                </Badge>
              )}
              {hasApprovedAmmount && (
                <div
                  className={styles[getClassName('amount')]}
                  data-testid={makeTestId('amount')}
                >
                  <FormattedCurrency
                    maximumFractionDigits={0}
                    minimumFractionDigits={2}
                    value={approvedAmount}
                  />
                </div>
              )}
              <div className={styles[getClassName(hasApprovedAmmount ? 'subtitle' : 'title')]}>{title}</div>
              {subtitle && <div className={styles[getClassName('subtitle')]}>{subtitle}</div>}
            </div>
            <div className={styles[getClassName('description')]}>{description}</div>
            <Divider className={styles[getClassName('divider')]} />
            <div>
              <ul className={styles[getClassName('bullet-list')]}>
                {bulletPoints.map((point, index) => (
                  <li
                    key={index}
                    className={styles[getClassName('bullet-item')]}
                  >
                    <Icon
                      className={styles[getClassName('bullet-icon')]}
                      name='check'
                    />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className={styles[getClassName('button-wrapper')]}>
            {hasTilaContent && (
              <BasicButton
                className={styles[getClassName('review-offer')]}
                data-testid={makeTestId('details')}
                disabled={!!tilaInfoError}
                name={formatMessage(
                  {
                    defaultMessage: 'Review offer summary {offerType}',
                    id: 'sputnik.EligibleOfferTile__2vQKp1',
                  },
                  { offerType: offerType },
                )}
                type={isMostPopular ? BASIC_BUTTON_TYPE_FILLED : BASIC_BUTTON_TYPE_OUTLINED}
                onClick={() => {
                  track('se_signup__eligible_offers_offer_tile_cta__fired', {
                    offer_type: getOfferTypeTrackingLabel(offerType),
                  })
                  setIsShowingAgreementTakeover(true)
                }}
              >
                <FormattedMessage
                  defaultMessage='Review offer summary'
                  id='sputnik.EligibleOfferTile__mZ4Qhn'
                />
              </BasicButton>
            )}
            {!hasTilaContent && (
              <BasicButton
                className={styles[getClassName('review-offer')]}
                data-testid={makeTestId('details')}
                disabled={!!tilaInfoError || !isLoggedInUserAuthorizedSigner}
                name={formatMessage(
                  {
                    defaultMessage: 'Select {offerType}',
                    id: 'sputnik.EligibleOfferTile__1Yk8sN',
                  },
                  { offerType: offerType },
                )}
                type={isMostPopular ? BASIC_BUTTON_TYPE_FILLED : BASIC_BUTTON_TYPE_OUTLINED}
                onClick={() => {
                  track('se_signup__eligible_offers_offer_tile_cta__fired', {
                    offer_type: getOfferTypeTrackingLabel(offerType),
                  })
                  setSelectedOffer(decision)
                }}
              >
                <FormattedMessage
                  defaultMessage='Select'
                  id='sputnik.EligibleOfferTile__oR3vGs'
                />
              </BasicButton>
            )}
            <EligibleOfferTileLearnMoreLink
              url={learnMoreLink}
              onClick={() =>
                track('se_signup__eligible_offers_learn_more__fired', {
                  offer_type: getOfferTypeTrackingLabel(offerType),
                })
              }
            />
          </div>
        </div>
      </Tile>

      {/* this also won't show if there is a tilaInfoError because if there is, it disables the button to open this */}
      {tilaContent && offerType && (
        <AgreementTakeover
          hasAgreedToTila={hasAgreedToTila}
          isAcceptingTilaAgreement={isAcceptingTilaAgreement}
          isLoggedInUserAuthorizedSigner={isLoggedInUserAuthorizedSigner}
          isShowing={isShowingAgreementTakeover}
          tilaContent={tilaContent}
          onAgreeClick={handleAgreeToOfferSummary}
          onCancel={handleCancelOfferSummaryClick}
        />
      )}
    </>
  )
}

export default EligibleOfferTile
